import { Injectable } from '@angular/core'
import { Select, State, Action, StateContext, Selector, NgxsOnInit, NgxsSimpleChange, Store } from '@ngxs/store'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { TimeEntryActions } from '../actions/time-entry.actions';
import { TimeEntry } from '../models/time-entry.model';
import { TimeEntryService } from 'src/app/services/time-entry.service';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { ConnectionStatusEnum, NetworkService } from 'src/app/services/network.service';
import { OfflineService } from 'src/app/services/offline.service';
import { DateTime } from 'luxon';
import { StorageService } from 'src/app/services/storage.service';
import { app_date_time_format } from 'src/app/globals';

export class TimeEntryStateModel {
    is_refreshing: boolean
    pending_count: number
    last_update: string
    time_entries: TimeEntry[]
}

@State<TimeEntryStateModel>({
    name: 'time_entry',
    defaults: {
        is_refreshing: true,
        pending_count: 0,
        last_update: '',
        time_entries: []
    }
})
@Injectable()
export class TimeEntryState implements NgxsOnInit {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(        
        private timeEntryService: TimeEntryService,        
        private networkService: NetworkService,
        private offline: OfflineService,
        private store: Store,
        private storageService: StorageService
    ) {}

    async ngxsOnInit(ctx: StateContext<TimeEntryStateModel>) {   
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {                 
                        await this.reloadStateFromStorage()   
                        this.store.dispatch(new TimeEntryActions.IncrementPendingCount())
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {            
        if (this.storageService.is_initialized) {      
            const state_model: TimeEntryStateModel = change.currentValue      
            this.storageService.set('time_entries', JSON.stringify(state_model))    
        }      
        if (this.networkService.currentStatus === ConnectionStatusEnum.Online) {            
            if (change.previousValue && change.previousValue.pending_count < change.currentValue.pending_count) {                
                const state_model: TimeEntryStateModel = change.currentValue
                await this.offline.processPendingTimeEntries(state_model.time_entries)                    
            } 
        }   
    }
    
    async reloadStateFromStorage() {        
        const json: string = await this.storageService.get('time_entries');        
        if (json) {
            const state_model: TimeEntryStateModel = JSON.parse(json)            
            if (state_model) {                
                this.store.dispatch(new TimeEntryActions.Init(state_model));
                return
            } 
        }
        this.store.dispatch(new TimeEntryActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {    
        const objs = await this.timeEntryService.getMine()    
        if (objs != null) {
          const many = TimeEntry.buildMany(objs)                     
          this.store.dispatch(new TimeEntryActions.Merge(many))
          return true
        }
        return false
    }
  
    @Selector()
    static getLastUpdate(state: TimeEntryStateModel) {
        return state.last_update
    }

    @Selector()
    static getIsRefreshing(state: TimeEntryStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getState(state: TimeEntryStateModel) {
        return state
    }

    @Selector()
    static getAll(state: TimeEntryStateModel) {
        return state.time_entries
    }

    @Selector()
    static getOne(state: TimeEntryStateModel) {
        return (id: number) => {
            return state.time_entries.filter(s => s.id === id)
        }
    }

    @Action(TimeEntryActions.Refresh)
    async refresh({patchState}: StateContext<TimeEntryStateModel>, {  }:TimeEntryActions.Refresh) {
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false})
        }
    }

    @Action(TimeEntryActions.IncrementPendingCount)
    incrementPendingCount({patchState, getState}: StateContext<TimeEntryStateModel>, {  }:TimeEntryActions.IncrementPendingCount) {     
        const state = getState()
        patchState({
            pending_count: state.pending_count + 1
        })
    }
    
    @Action(TimeEntryActions.Init)
    init({setState, getState}: StateContext<TimeEntryStateModel>, { payload }:TimeEntryActions.Init) {                  
        const state = getState()
        payload.is_refreshing = state.is_refreshing
        setState(payload)
        this.store.dispatch(new TimeEntryActions.Refresh())
    }
    
    @Action(TimeEntryActions.Set)
    set({setState, getState}: StateContext<TimeEntryStateModel>, { payload }:TimeEntryActions.Set) {      
        const state = getState()        
        setState({   
            is_refreshing: state.is_refreshing, 
            pending_count: 0,
            last_update: DateTime.now().toFormat(app_date_time_format),         
            time_entries: payload
        })
    }

    @Action(TimeEntryActions.Merge)
    merge({getState, setState}: StateContext<TimeEntryStateModel>, { payload }:TimeEntryActions.Merge) {   
        // This is to merge data from the API provided in payload, into the app state.
        // Basically, if we have server data, we trust that, so we want to replace the 
        // current app state with the payload, except:
        // 1. Any objects pending upload or delete need to be merged in, even if it no longer exists.
        // 2. Same goes for any with a service_entry that is pending approval upload.
        const state = getState()
        const oldTimeEntries = [...state.time_entries]
        let newTimeEntries = [...payload]      

        // For each wor in state now...
        for (const time_entry of oldTimeEntries) {     
            // If wor id is not in the list from the server       
            let newTimeEntry = payload.find(f => f.id === time_entry.id)   
            if (!newTimeEntry) {                
                // If there are any pending save/delete, keep the entire object in state, else do not since no longer on server                
                if (time_entry.is_pending_save || time_entry.is_pending_delete) {                 
                    newTimeEntries.push(time_entry)
                }
            } else {                
                // Object in state is in the payload from the server. 
                // 1. If anything is pending, we need to preserve the one in state, first removing the one that is from the server
                // 1. Else we leave the one from the server to set in the new state
                if (time_entry.is_pending_save || time_entry.is_pending_delete) {
                    // Remove one that is from the server
                    newTimeEntries = newTimeEntries.filter((obj) => {return obj !== newTimeEntry});                        
                    // Add the one from state back in
                    newTimeEntries.push(time_entry)
                }
            }
        }

        setState({
            is_refreshing: false,
            pending_count: 0,
            last_update: DateTime.now().toFormat(app_date_time_format),
            time_entries: newTimeEntries
        })
    }

    @Action(TimeEntryActions.AddOne)
    addOne({getState, patchState}: StateContext<TimeEntryStateModel>, { payload }:TimeEntryActions.AddOne) {
        const state = getState()              
        patchState({
            time_entries: [...state.time_entries, payload]
        })
    }

    @Action(TimeEntryActions.UpdateOne)
    updateOne({setState}: StateContext<TimeEntryStateModel>, { payload }:TimeEntryActions.UpdateOne) {
        setState(
            patch<TimeEntryStateModel>
                ({
                    time_entries: updateItem((item: TimeEntry) => item.id === payload.id, 
                        patch<TimeEntry>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(TimeEntryActions.DeleteOne)
    deleteOne({setState}: StateContext<TimeEntryStateModel>, { payload }:TimeEntryActions.DeleteOne) {
        setState(
            patch<TimeEntryStateModel>
                ({
                    time_entries: updateItem((item: TimeEntry) => item.id === payload.id, 
                        patch<TimeEntry>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(TimeEntryActions.ApiAddedOne)
    apiAddedOne({ setState }: StateContext<TimeEntryStateModel>, { payload }:TimeEntryActions.ApiAddedOne) {         
        setState(
            patch<TimeEntryStateModel>
                ({
                    time_entries: updateItem((item: TimeEntry) => item.uuid === payload.uuid, 
                        patch<TimeEntry>
                            ({
                                id: payload.id,
                                is_current_pay_period: payload.is_current_pay_period,
                                is_pending_save: false                           
                            })
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(TimeEntryActions.ApiUpdatedOne)
    apiUpdatedOne({ setState }: StateContext<TimeEntryStateModel>, { payload }:TimeEntryActions.ApiUpdatedOne) {         
        setState(
            patch<TimeEntryStateModel>
                ({
                    time_entries: updateItem((item: TimeEntry) => item.id === payload.id, 
                        patch<TimeEntry>
                            ({
                                is_pending_save: false                           
                            })
                        ),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }

    @Action(TimeEntryActions.ApiDeletedOne)
    apiDeletedOne({ setState }: StateContext<TimeEntryStateModel>, { payload }:TimeEntryActions.ApiDeletedOne) {         
        setState(
            patch<TimeEntryStateModel>
                ({
                    time_entries: removeItem((item: TimeEntry) => item.id === payload.id),
                    last_update: DateTime.now().toFormat(app_date_time_format)                 
                })                
        )
    }
}
